import { createContext, use, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useIntl } from 'react-intl'
import { useLocation } from 'react-router'

import { Toast } from '@/shared/ui'

/** Says that a change the person made worked: shown as a toast and announced politely, without moving focus. */
export interface Confirmation {
  /** "Saved." */
  readonly saved: () => void
  /** "<record type> deleted.", such as "Badge deleted."; `recordType` is the record's kind, as its page's eyebrow names it. */
  readonly deleted: (recordType: string) => void
}

const ignored: Confirmation = { saved: () => undefined, deleted: () => undefined }

// Outside the app shell, as in a page's own test, there is nothing to say it.
const ConfirmationContext = createContext<Confirmation>(ignored)

export function useConfirmation(): Confirmation {
  return use(ConfirmationContext)
}

/** How long focus must stay put before the confirmation is said, so a heading focused after it does not cut it off. */
export const confirmationSettleMs = 300
/** How long it waits for focus to move at all, as when the page stays where it is. */
export const confirmationFocusWaitMs = 1000
/** How long it waits, after a navigation or while the page reads its record again, for the page's heading to take focus. */
export const confirmationNavigationWaitMs = 5000

/** Whether the page is reading its record, as a details page does again after a delete that keeps it: it shows no heading until then. */
function isPageLoading(): boolean {
  const main = document.querySelector('main')
  return main !== null && main.querySelector('h1') === null
}

interface Shown {
  readonly id: number
  readonly text: string
}

/**
 * Holds the one confirmation toast every page shares, so a confirmation outlives the page that
 * asked for it, as a save that goes on to the record's details. It is said once focus has settled,
 * such as on the next page's heading or back on the control that opened a dialog, so a screen
 * reader reads it after what took focus rather than being cut off by it.
 */
export function ConfirmationProvider({ children }: { readonly children: ReactNode }) {
  const intl = useIntl()
  const { key } = useLocation()
  const location = useRef(key)
  useEffect(() => {
    location.current = key
  }, [key])
  const [shown, setShown] = useState<Shown | null>(null)
  const count = useRef(0)
  const stopWaiting = useRef<(() => void) | null>(null)
  useEffect(() => () => stopWaiting.current?.(), [])

  const confirm = useCallback((text: string) => {
    stopWaiting.current?.()
    // Emptied first, so the same words said again are announced again.
    setShown(null)
    const askedAt = Date.now()
    const askedOn = location.current
    let timer = 0
    const show = () => {
      stop()
      count.current += 1
      setShown({ id: count.current, text })
    }
    // With no focus move, it is said soon; after a navigation, or while the page reads its record
    // again, once the page's heading is back and takes focus.
    const showOrWait = (waited: boolean) => {
      const elapsed = Date.now() - askedAt
      if (elapsed < confirmationNavigationWaitMs && ((waited && location.current !== askedOn) || isPageLoading())) {
        waitForFocus(Math.min(confirmationFocusWaitMs, confirmationNavigationWaitMs - elapsed))
        return
      }
      show()
    }
    const waitForFocus = (ms: number) => {
      window.clearTimeout(timer)
      timer = window.setTimeout(() => showOrWait(true), ms)
    }
    const settle = () => {
      window.clearTimeout(timer)
      timer = window.setTimeout(() => showOrWait(false), confirmationSettleMs)
    }
    function stop() {
      window.clearTimeout(timer)
      document.removeEventListener('focusin', settle)
      stopWaiting.current = null
    }
    document.addEventListener('focusin', settle)
    stopWaiting.current = stop
    waitForFocus(confirmationFocusWaitMs)
  }, [])

  const confirmation = useMemo<Confirmation>(
    () => ({
      saved: () => confirm(intl.formatMessage({ id: 'shell.confirmation.saved' })),
      deleted: (recordType) => confirm(intl.formatMessage({ id: 'shell.confirmation.deleted' }, { recordType })),
    }),
    [confirm, intl],
  )
  const dismiss = useCallback(() => setShown(null), [])

  return (
    <ConfirmationContext value={confirmation}>
      {children}
      <Toast isOpen={shown !== null} onDismiss={dismiss}>
        {/* Keyed, so each confirmation is new text to a screen reader even when the words repeat. */}
        <span key={shown?.id}>{shown?.text}</span>
      </Toast>
    </ConfirmationContext>
  )
}
